import { TMachineCategoryPageDataForPDP } from '@internal/page/TMachineCategoryPageData'
import { Machine } from '@internal/utils/machine/Machine'
import { getSearchResultsUrl } from '@internal/utils/routing/getSearchResultsUrl'
import { ArrowBack } from '@mui/icons-material'
import { Button } from '@renderer-ui-library/atoms'
import { Typography } from '@renderer-ui-library/mui'
import { TLocale, localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { SystemPageKey } from 'page/SystemPageKey'
import React, { FC, memo, useCallback, useContext, useMemo } from 'react'
import { useMachineTypeName } from 'utils/machine/useMachineTypeName'
import { PageTypeKey } from 'utils/page/PageTypeKey'
import { BrowserHistoryStackContext } from 'utils/routing/BrowserHistoryStackContext'
import { latestStackEntryIndex } from 'utils/routing/hooks/useBrowserHistoryStack'
import styles from './machineOverviewBlock.module.scss'

type Props = {
  machine: Machine
  locale: TLocale
  machineCategoryPages: {
    forMachineType: TMachineCategoryPageDataForPDP | null
    forMachineTypeAndBrand: TMachineCategoryPageDataForPDP | null
    forMachineTypeBrandAndModel: TMachineCategoryPageDataForPDP | null
  }
}

const forceHistoryBackAction = '#'

export const BackButton: FC<Props> = memo(
  ({ machine, machineCategoryPages, locale }) => {
    const { browserHistoryStack } = useContext(BrowserHistoryStackContext)
    const { t } = useTranslation(localeNamespace.common)
    const { getMachineTypeName } = useMachineTypeName()
    const { back } = useRouter()

    const buttonProps: { label: string; url: string } = useMemo(() => {
      if (!machine) {
        return {
          label: t(translations.machineOverviewBackButtonLabel),
          url: forceHistoryBackAction,
        }
      }
      const isComingFromSearchResultsPage = !!(
        browserHistoryStack[latestStackEntryIndex] &&
        browserHistoryStack[latestStackEntryIndex].pageType?.type ===
          PageTypeKey.SystemPage &&
        browserHistoryStack[latestStackEntryIndex].pageType.key ===
          SystemPageKey.SearchResults
      )

      const isComingFromMachineCategoryPage = !!(
        browserHistoryStack[latestStackEntryIndex] &&
        browserHistoryStack[latestStackEntryIndex].pageType?.type ===
          PageTypeKey.MachineCategoryPage
      )

      const machineCategoryPage =
        (machineCategoryPages.forMachineTypeBrandAndModel?.localizations[
          locale
        ] &&
          machineCategoryPages.forMachineTypeBrandAndModel) ||
        (machineCategoryPages.forMachineTypeAndBrand?.localizations[locale] &&
          machineCategoryPages.forMachineTypeAndBrand) ||
        (machineCategoryPages.forMachineType?.localizations[locale] &&
          machineCategoryPages.forMachineType)

      if (isComingFromSearchResultsPage || isComingFromMachineCategoryPage) {
        return {
          label: t(translations.machineOverviewBackButtonLabel),
          url: forceHistoryBackAction,
        }
      } else if (machineCategoryPage) {
        return {
          label: t(translations.machineOverviewBackButtonLabelWithPlaceholder, {
            target: [
              machineCategoryPage.brand,
              machineCategoryPage.model,
              machineCategoryPage.machineType &&
                getMachineTypeName(machineCategoryPage.machineType, 'plural'),
            ]
              .filter((s): s is string => !!s)
              .join(' '),
          }),
          url: machineCategoryPage?.localizations[locale]!,
        }
      } else {
        return {
          label: t(translations.machineOverviewBackButtonLabel),
          url: getSearchResultsUrl(locale, {
            brands: [machine.brand],
            machineTypeName: getMachineTypeName(machine.machineType, 'plural'),
          }),
        }
      }
    }, [
      browserHistoryStack,
      getMachineTypeName,
      locale,
      machine,
      machineCategoryPages,
      t,
    ])

    const handleClick = useCallback(
      (ev: React.MouseEvent) => {
        if (buttonProps.url === forceHistoryBackAction) {
          ev.preventDefault()
          back()
        }
      },
      [buttonProps, back]
    )

    return (
      <Link
        href={buttonProps.url}
        passHref
        prefetch={false}
        onClick={handleClick}
      >
        <Button
          variant='text'
          size='small'
          noMinWidth
          startIcon={<ArrowBack className={styles.arrowIcon} />}
          color='inherit'
        >
          <Typography
            variant='body2'
            fontSize={{ xs: 'body2.mobile', md: 'body1.fontSize' }}
            color='text.secondary'
          >
            {buttonProps.label}
          </Typography>
        </Button>
      </Link>
    )
  }
)

BackButton.displayName = 'BackButton'
